import { useRef, useEffect, useLayoutEffect, useCallback, RefObject } from 'react';
import { computeTrackOffsets } from '../constants';
import type { Tab } from '../../types';

const SWIPE_THRESHOLD_PX = 50;
const DIRECTION_LOCK_THRESHOLD_PX = 5;
const RUBBER_BAND_FACTOR = 0.3;

interface UseCarouselGesturesOptions {
  tabs: Tab[];
  initialTabIndex: number;
  onTabChange: (index: number) => void;
}

interface UseCarouselGesturesResult {
  trackRef: RefObject<HTMLDivElement | null>;
  viewportRef: RefObject<HTMLDivElement | null>;
  goToTab: (index: number) => void;
}

/**
 * Manages carousel track positioning, touch-swipe gestures, and viewport
 * resize handling. Returns refs that must be attached to the track and
 * viewport DOM elements, and a `goToTab` imperative.
 */
const useCarouselGestures = ({
  tabs,
  initialTabIndex,
  onTabChange,
}: UseCarouselGesturesOptions): UseCarouselGesturesResult => {
  const trackRef = useRef<HTMLDivElement>(null);
  const viewportRef = useRef<HTMLDivElement>(null);

  // Refs used inside event-handler closures to avoid stale captures.
  const activeTabRef = useRef(initialTabIndex);
  const trackOffsetsRef = useRef(computeTrackOffsets(window.innerWidth));

  // Set the initial track position before the first paint to avoid a FOUC
  // and to keep positioning 100% imperative (never via JSX inline style).
  useLayoutEffect(() => {
    if (trackRef.current) {
      const offsets = computeTrackOffsets(window.innerWidth);
      trackOffsetsRef.current = offsets;
      trackRef.current.style.transition = 'none';
      trackRef.current.style.transform = `translateX(${offsets[initialTabIndex]}px)`;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Recalculate offsets on resize / orientation change and snap to current tab.
  useEffect(() => {
    const handleResize = () => {
      const offsets = computeTrackOffsets(window.innerWidth);
      trackOffsetsRef.current = offsets;
      if (trackRef.current) {
        trackRef.current.style.transition = 'none';
        trackRef.current.style.transform = `translateX(${offsets[activeTabRef.current]}px)`;
      }
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const goToTab = useCallback(
    (index: number) => {
      activeTabRef.current = index;
      onTabChange(index);
      if (trackRef.current) {
        trackRef.current.style.transition = 'transform 300ms ease';
        trackRef.current.style.transform = `translateX(${trackOffsetsRef.current[index]}px)`;
      }
    },
    [onTabChange]
  );

  // Attach touch event listeners directly on the DOM node so we can call
  // preventDefault() on touchmove (React 16 synthetic events are passive).
  useEffect(() => {
    const viewport = viewportRef.current;
    if (!viewport) return;

    let startX = 0;
    let startY = 0;
    let swipeDirection: 'horizontal' | 'vertical' | null = null;
    let isTracking = false;

    const onTouchStart = (e: TouchEvent) => {
      startX = e.touches[0].clientX;
      startY = e.touches[0].clientY;
      swipeDirection = null;
      isTracking = true;
      if (trackRef.current) trackRef.current.style.transition = 'none';
    };

    const onTouchMove = (e: TouchEvent) => {
      if (!isTracking) return;

      const deltaX = e.touches[0].clientX - startX;
      const deltaY = e.touches[0].clientY - startY;

      if (!swipeDirection) {
        if (Math.abs(deltaX) > Math.abs(deltaY) + DIRECTION_LOCK_THRESHOLD_PX) {
          swipeDirection = 'horizontal';
        } else if (Math.abs(deltaY) > Math.abs(deltaX) + DIRECTION_LOCK_THRESHOLD_PX) {
          swipeDirection = 'vertical';
        }
      }

      if (swipeDirection === 'horizontal') {
        e.preventDefault();
        const tab = activeTabRef.current;
        const baseX = trackOffsetsRef.current[tab];
        const isAtStart = tab === 0 && deltaX > 0;
        const isAtEnd = tab === tabs.length - 1 && deltaX < 0;
        const dragX = isAtStart || isAtEnd ? deltaX * RUBBER_BAND_FACTOR : deltaX;
        if (trackRef.current) {
          trackRef.current.style.transform = `translateX(${baseX + dragX}px)`;
        }
      }
    };

    const onTouchEnd = (e: TouchEvent) => {
      if (!isTracking) return;
      isTracking = false;

      if (swipeDirection === 'horizontal') {
        const deltaX = e.changedTouches[0].clientX - startX;
        const tab = activeTabRef.current;

        if (deltaX < -SWIPE_THRESHOLD_PX && tab < tabs.length - 1) {
          goToTab(tab + 1);
        } else if (deltaX > SWIPE_THRESHOLD_PX && tab > 0) {
          goToTab(tab - 1);
        } else {
          goToTab(tab); // snap back if swipe didn't cross the threshold
        }
      }

      swipeDirection = null;
    };

    viewport.addEventListener('touchstart', onTouchStart, { passive: true });
    viewport.addEventListener('touchmove', onTouchMove, { passive: false });
    viewport.addEventListener('touchend', onTouchEnd, { passive: true });

    return () => {
      viewport.removeEventListener('touchstart', onTouchStart);
      viewport.removeEventListener('touchmove', onTouchMove);
      viewport.removeEventListener('touchend', onTouchEnd);
    };
  }, [goToTab, tabs.length]);

  return { trackRef, viewportRef, goToTab };
};

export default useCarouselGestures;
